// Public median-rate feed for the Directory ticker.
//
// popMarketRatesAggregator (functions/lib/pop-market.js) writes
// pop_market_meta/global_stats.marketRates nightly:
//
//   marketRates: {
//     "BGMI_mix_INR":        { median, p25, p75, sampleSize },
//     "BGMI_lumen_INR":      { ... },
//     "BGMI_helicopter_INR": { ... },
//     "BGMI_premium_INR":    { ... },
//     "PUBGM_mix_INR":       { ... },
//     ...
//   }
//
// This module fetches that once per session (with a short localStorage
// cache so page-flips don't re-hit Firestore) and returns the row for
// a given { game, currency }.

import { doc, getDoc } from "firebase/firestore";
import { db } from "../firebase";
import { SEGMENT_KEYS } from "./constants";

const CACHE_KEY = "popmarket-global-stats-v1";
const CACHE_TTL_MS = 30 * 60 * 1000; // 30 min — daily aggregator can churn once

// { marketRates, lastUpdatedAt (ms since epoch), fetchedAt (ms since epoch) }
export async function loadGlobalStats() {
  const cached = readCache();
  if (cached && Date.now() - cached.fetchedAt < CACHE_TTL_MS) return cached;

  try {
    const snap = await getDoc(doc(db, "pop_market_meta", "global_stats"));
    if (!snap.exists()) return null;
    const d = snap.data();
    const record = {
      marketRates: d.marketRates || {},
      lastUpdatedAt: d.lastUpdatedAt?.toDate?.()?.getTime?.() ?? null,
      fetchedAt: Date.now(),
    };
    writeCache(record);
    return record;
  } catch (e) {
    console.warn("[pop-market] loadGlobalStats failed", e);
    return cached || null; // fall back to whatever's in cache, even stale
  }
}

// Extract the per-segment row for one game+currency combo. Returns null
// if we've never seen data. Returns partial rows if some segments have
// no samples (e.g. Premium is rarely priced by base rate).
//
//   rowFor(stats, "BGMI", "INR")
//     → { mix: {median,p25,p75,sampleSize}, lumen: {…}, … }
export function rowFor(stats, game, currency = "INR") {
  if (!stats?.marketRates) return null;
  const row = {};
  let any = false;
  for (const seg of SEGMENT_KEYS) {
    const bucket = stats.marketRates[`${game}_${seg}_${currency}`];
    if (bucket && isFinite(bucket.median)) {
      row[seg] = bucket;
      any = true;
    }
  }
  return any ? row : null;
}

function readCache() {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.fetchedAt) return null;
    return parsed;
  } catch { return null; }
}
function writeCache(record) {
  try { localStorage.setItem(CACHE_KEY, JSON.stringify(record)); }
  catch { /* ignore quota errors */ }
}
